/**
 * 屏幕 4：捕获图鉴（怪兽圈养园）
 * 2×2 玻璃房，每个房按体型对应 biome（水/空/地）
 * 怪物依据 biome 做不同的悬浮/浮动动画
 */

import { createSnow } from "../snow";
import { generateMonster, animateIdle, type MonsterFrames } from "../spriteLib";
import { getZoo, getBiome, BIOME_LABELS, type Biome, type ZooEntry } from "../zoo";
import type { Screen2Combo } from "./screen2";

export interface Screen4Deps {
  root: HTMLElement;
  onBack: () => void;
}

export interface Screen4Controller {
  destroy: () => void;
}

const HOUSE_SIZE = 220;

export function createScreen4(deps: Screen4Deps): Screen4Controller {
  const { root, onBack } = deps;

  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-4";

  const title = document.createElement("div");
  title.className = "title";
  title.textContent = "捕获图鉴";
  screen.appendChild(title);

  const grid = document.createElement("div");
  grid.className = "zoo-grid";
  screen.appendChild(grid);

  const entries = getZoo();

  if (entries.length === 0) {
    const empty = document.createElement("div");
    empty.className = "zoo-empty";
    empty.innerHTML = "图鉴还是空的<br/><small>去主页生成 + 选中一只怪，它就会住进玻璃房</small>";
    grid.appendChild(empty);
  } else {
    entries.forEach((entry) => {
      const cell = createGlassHouse(entry);
      grid.appendChild(cell);
    });
  }

  // 悬浮像素返回按钮（右下角，在 BGM 按钮上方）
  const backBtn = document.createElement("button");
  backBtn.className = "screen4-back";
  backBtn.title = "返回主屏";
  backBtn.innerHTML = `
    <svg viewBox="0 0 16 16" fill="currentColor">
      <rect x="12" y="3" width="2" height="2"/>
      <rect x="11" y="4" width="2" height="2"/>
      <rect x="10" y="5" width="2" height="2"/>
      <rect x="9" y="6" width="2" height="2"/>
      <rect x="2" y="7" width="10" height="2"/>
      <rect x="9" y="8" width="2" height="2"/>
      <rect x="10" y="9" width="2" height="2"/>
      <rect x="11" y="10" width="2" height="2"/>
      <rect x="12" y="11" width="2" height="2"/>
    </svg>
  `;
  backBtn.addEventListener("click", () => onBack());
  screen.appendChild(backBtn);

  root.appendChild(screen);

  const destroy = () => {
    destroySnow();
    screen.remove();
  };

  return { destroy };
}

function createGlassHouse(entry: ZooEntry): HTMLElement {
  const biome = getBiome(entry.combo.bodyType);

  const house = document.createElement("div");
  house.className = `glass-house biome-${biome}`;

  // 标签（顶部）
  const label = document.createElement("div");
  label.className = "house-label";
  label.textContent = entry.monster.scpId;
  house.appendChild(label);

  // 主画布
  const canvas = document.createElement("canvas");
  canvas.width = HOUSE_SIZE;
  canvas.height = HOUSE_SIZE;
  house.appendChild(canvas);

  // 怪物体
  const portrait = document.createElement("div");
  portrait.className = "house-portrait";
  portrait.textContent = entry.monster.class;
  house.appendChild(portrait);

  // 渲染
  const frames = generateMonster(
    entry.monster,
    undefined,
    entry.combo.bodyType,
    entry.combo.paletteName,
    entry.combo.variant
  );

  animateHouse(canvas, frames, biome);

  return house;
}

/**
 * 房屋画布动画：先画 biome 背景，再画怪物，最后画玻璃效果
 */
function animateHouse(
  canvas: HTMLCanvasElement,
  frames: MonsterFrames,
  biome: Biome
): void {
  const ctx = canvas.getContext("2d")!;
  const W = canvas.width;
  const H = canvas.height;
  const FRAME_W = 64;
  const SCALED = 192; // 怪物在画布中的尺寸

  // 预渲染怪物两帧到临时画布（避免每帧重画 64×64 像素）
  const f1 = document.createElement("canvas");
  f1.width = FRAME_W;
  f1.height = FRAME_W;
  f1.getContext("2d")!.putImageData(frames.frame1, 0, 0);
  const f2 = document.createElement("canvas");
  f2.width = FRAME_W;
  f2.height = FRAME_W;
  f2.getContext("2d")!.putImageData(frames.frame2, 0, 0);

  let frame = 0;
  let running = true;
  function tick() {
    if (!running) return;
    const t = performance.now() / 1000;

    // 1. 背景
    drawBiome(ctx, W, H, t, biome);

    // 2. 怪物位置（按 biome 决定运动）
    let dx = 0;
    let dy = 0;
    let scaleB = 1;
    switch (biome) {
      case "water":
        dy = Math.sin(t * 2.0) * 6;
        dx = Math.sin(t * 0.7) * 3;
        break;
      case "air":
        dy = Math.sin(t * 1.4) * 10 - 6;
        dx = Math.sin(t * 0.6) * 5;
        scaleB = 0.95 + Math.sin(t * 1.4) * 0.04;
        break;
      case "ground":
        dy = 6; // 站在地上方
        dx = Math.sin(t * 0.3) * 2;
        break;
    }
    const cx = W / 2 + dx - SCALED / 2;
    const cy = H / 2 + dy - SCALED / 2 + 4;

    // 3. 画怪物（带 idle 2 帧切换）
    const useFrame = frame % 2 === 0 ? f1 : f2;
    const sz = SCALED * scaleB;
    const offX = (W - sz) / 2 + dx;
    const offY = (H - sz) / 2 + dy + 4;
    ctx.drawImage(useFrame, offX, offY, sz, sz);

    // 4. 玻璃效果（半透 + 高光 + 阴影）
    drawGlassOverlay(ctx, W, H);

    frame++;
    requestAnimationFrame(tick);
  }
  tick();
}

function drawBiome(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  t: number,
  biome: Biome
): void {
  // 渐变背景
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  switch (biome) {
    case "water":
      grad.addColorStop(0, "#1a3d5f");
      grad.addColorStop(0.5, "#2a5d8f");
      grad.addColorStop(1, "#4a8db8");
      break;
    case "air":
      grad.addColorStop(0, "#2a4a6a");
      grad.addColorStop(0.6, "#5a7a9a");
      grad.addColorStop(1, "#a0c0e0");
      break;
    case "ground":
      grad.addColorStop(0, "#3a2a1a");
      grad.addColorStop(0.5, "#5a3a2a");
      grad.addColorStop(1, "#7a5a4a");
      break;
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // biome 装饰
  switch (biome) {
    case "water":
      drawWaterDecor(ctx, W, H, t);
      break;
    case "air":
      drawAirDecor(ctx, W, H, t);
      break;
    case "ground":
      drawGroundDecor(ctx, W, H);
      break;
  }
}

function drawWaterDecor(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
  // 3 条波纹（横向流动）
  ctx.strokeStyle = "rgba(170, 220, 255, 0.5)";
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    const baseY = H * 0.25 + i * (H * 0.22);
    for (let x = -10; x < W + 10; x += 4) {
      const yy = baseY + Math.sin((x * 0.04) + t * 1.5 + i * 1.2) * 3;
      if (x === -10) ctx.moveTo(x, yy);
      else ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }
  // 几个小气泡
  ctx.fillStyle = "rgba(220, 240, 255, 0.6)";
  const bubbles = [[0.2, 0.3], [0.7, 0.6], [0.3, 0.8], [0.85, 0.35]];
  for (const [px, py] of bubbles) {
    const bx = W * px + Math.sin(t * 1.0 + px * 10) * 5;
    const by = H * py + Math.cos(t * 0.8 + py * 10) * 3;
    ctx.beginPath();
    ctx.arc(bx, by, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawAirDecor(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
  // 3 朵云（横向漂移）
  ctx.fillStyle = "rgba(200, 224, 240, 0.7)";
  for (let i = 0; i < 3; i++) {
    const offset = (t * 12 + i * 140) % (W + 80);
    const x = offset - 40;
    const y = 25 + i * 50 + Math.sin(t * 0.5 + i) * 4;
    drawCloud(ctx, x, y);
  }
  // 几只小鸟（远处剪影）
  ctx.fillStyle = "rgba(80, 60, 60, 0.6)";
  for (let i = 0; i < 4; i++) {
    const x = (i * 90 + 30 + Math.sin(t * 0.3 + i) * 10);
    const y = 80 + i * 40;
    drawBird(ctx, x, y);
  }
}

function drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.beginPath();
  ctx.arc(x, y, 14, 0, Math.PI * 2);
  ctx.arc(x + 16, y - 4, 12, 0, Math.PI * 2);
  ctx.arc(x + 30, y, 13, 0, Math.PI * 2);
  ctx.arc(x + 14, y + 6, 12, 0, Math.PI * 2);
  ctx.fill();
}

function drawBird(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.beginPath();
  ctx.moveTo(x - 6, y);
  ctx.quadraticCurveTo(x - 3, y - 4, x, y);
  ctx.quadraticCurveTo(x + 3, y - 4, x + 6, y);
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawGroundDecor(ctx: CanvasRenderingContext2D, W: number, H: number) {
  // 地面（底部 30%）
  ctx.fillStyle = "#4a2a1a";
  ctx.fillRect(0, H * 0.7, W, H * 0.3);
  // 地面纹理（散点）
  ctx.fillStyle = "#6a4a2a";
  for (let i = 0; i < 30; i++) {
    const x = (i * 53) % W;
    const y = H * 0.7 + ((i * 17) % (H * 0.3));
    ctx.fillRect(x, y, 2, 2);
  }
  // 几株小草
  for (let i = 0; i < 4; i++) {
    const x = 30 + i * (W / 4);
    drawGrass(ctx, x, H * 0.7);
  }
}

function drawGrass(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.strokeStyle = "#6a8a3a";
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(x + i * 4 - 4, y);
    ctx.quadraticCurveTo(x + i * 4 - 4, y - 8, x + i * 4 - 6, y - 10);
    ctx.stroke();
  }
}

function drawGlassOverlay(ctx: CanvasRenderingContext2D, W: number, H: number) {
  // 半透叠加（整体轻微白色）
  ctx.fillStyle = "rgba(255, 255, 255, 0.04)";
  ctx.fillRect(0, 0, W, H);

  // 顶部高光（玻璃反光）
  ctx.fillStyle = "rgba(255, 255, 255, 0.18)";
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(W * 0.6, 0);
  ctx.lineTo(W * 0.4, H * 0.15);
  ctx.lineTo(0, H * 0.25);
  ctx.closePath();
  ctx.fill();

  // 左上小高光（点状）
  ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
  ctx.beginPath();
  ctx.moveTo(8, 8);
  ctx.lineTo(20, 8);
  ctx.lineTo(14, 14);
  ctx.closePath();
  ctx.fill();

  // 右下阴影
  ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
  ctx.beginPath();
  ctx.moveTo(W, H);
  ctx.lineTo(W, H * 0.7);
  ctx.lineTo(W * 0.7, H);
  ctx.closePath();
  ctx.fill();
}
